import {resolveApiPath} from '@/services/infrastructure/ServerBasePathResolver'
import {getAccessToken} from '@/services/infrastructure/auth.service'
import {getSelectedOrgId, myOrgs} from '@/tenants/org.service'
import {Either, Left, Right} from '@/types/either/Either'
import {Problem} from '@/types/Problem'
import axios, {AxiosError} from 'axios'

const getBankApiBase = () => `${resolveApiPath()}/banking`

export const bankingUrl = window.location.protocol + '//banking.' + window.location.hostname.split('.').slice(1).join('.')

const getHeaders = async (tenantId?: string): Promise<any> => {
    const accessToken = await getAccessToken()
    if (!accessToken) {
        console.error('Attempting to call backend API without a valid access token! Should not happen!')
        return
    }

    const tenantHeader = tenantId || getSelectedOrgId()
    const currentRoles = myOrgs.value?.find(org => org.id === tenantHeader)?.roles || []

    return {
        Authorization: `Bearer ${accessToken}`,
        tenantId: tenantHeader,
        'Conta-Tenant-User-Roles': btoa(currentRoles.join(',')) // Invalidate cache at banking
    }
}

const doCatch = async <VALUE_TYPE>(block: () => Promise<VALUE_TYPE>): Promise<Either<Problem, VALUE_TYPE>> => {
    try {
        return Right.create(await block())
    } catch (e) {
        if (e instanceof AxiosError && e.response?.data) {
            console.error(e)
            return Left.create(e.response.data)
        }
        throw e
    }
}

const post = async (path: string, body: any, headers: any = {}, params: any = {}) => {
    const response = await axios.post(
        getBankApiBase() + path,
        body,
        {
            headers: {
                ...await getHeaders(),
                ...headers
            },
            params: {
                ...Object.fromEntries(Object.entries(params).filter(([, v]) => v != null))
            }
        }
    )

    //TODO: Error handling
    return response.data
}

const postCatching = async <T>(path: string, body: any, headers: any = {}, params: any = {}): Promise<Either<Problem, T>> => {
    return doCatch(async () => {
        const response = await axios.post(
            getBankApiBase() + path,
            body,
            {
                headers: {
                    ...await getHeaders(),
                    ...headers
                },
                params: {
                    ...Object.fromEntries(Object.entries(params).filter(([, v]) => v != null))
                }
            }
        )

        return response.data
    })
}

const getCatching = async <T>(path: string, params: any = {}, tenantId?: string): Promise<Either<Problem, T>> => {
    return doCatch(async () => {
        const response = await axios.get(
            getBankApiBase() + path,
            {
                headers: await getHeaders(tenantId),
                params: {
                    ...Object.fromEntries(Object.entries(params).filter(([, v]) => v != null))
                }
            }
        )
        return response.data
    })
}

//TODO: instead of using tenantId as optional parameter, use a full headers-object and allow overwriting with destructuring (like in POST)
const get = async (path: string, params: any = {}, tenantId?: string) => {
    try {
        const response = await axios.get(
            getBankApiBase() + path,
            {
                headers: await getHeaders(tenantId),
                params: {
                    ...Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined))
                }
            }
        )
        return response.data
    } catch (e: any) {
        console.error(e)
    }
}

const put = async (path: string, body: any, headers: any = {}) => {
    const response = await axios.put(
        getBankApiBase() + path,
        body,
        {
            headers: {
                ...await getHeaders(),
                ...headers
            }
        }
    )

    //TODO: Error handling
    return response.data
}

const httpDelete = async (path: string, headers: any = {}) => {
    const response = await axios.delete(
        getBankApiBase() + path,
        {
            headers: {
                ...await getHeaders(),
                ...headers
            }
        }
    )

    //TODO: Error handling
    return response.data
}

const patch = async (path: string, body: any, headers: any = {}): Promise<Either<Problem, any>> => {
    return doCatch(async () => {
        const response = await axios.patch(
            getBankApiBase() + path,
            body,
            {
                headers: {
                    ...await getHeaders(),
                    ...headers
                }
            }
        )

        return response.data
    })
}

export {get, getCatching, post, postCatching, put, httpDelete, patch}